import React, { useState, useEffect } from "react";
import { getTweetsVolumeData } from "../../../services/dashboard";
import { getPercentageDifference } from "../../../utils/functions/getPercentageDifference";
import { migTweets as texts_es } from "../../../utils/text/graphTexts";
import { migTweets as texts_en } from "../../../utils/text/graphTexts_en";
import {
  generateLineToolTip,
  generateMultiplLineTooltip,
} from "../../../utils/functions/generateLineTooltip";
import { getPreviousMonth } from "../../../utils/functions/getPreviousMonth";
import { useParams } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  getCountriesAsSelectOptions,
} from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";
import {
  getMonthlyThematicEvolution,
  getMonthlyTweetVolume,
} from "../../../services/singleMonths";
import {
  getSimpleLineConfig,
  GRID_CONFIG,
  GRID_CONFIG_WITH_LEGEND,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { seriesValues } from "../../../utils/functions/seriesValues";
import { transformNameCountries } from "../../../utils/functions/transformNameCountries";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { isCountryIncluded } from "../../../utils/functions/isCountryIncluded";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function Tweets({ selectedCountry, colorRBG }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("tweets");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    selectedCountry
      ? COUNTRY_OPTIONS.find((opt) => {
          return opt.code === selectedCountry;
        })
      : COUNTRY_OPTIONS.find((opt) => {
          return opt.code === country;
        })
  );
  // const [dateOptions, setDateOptions] = useState( d);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(
    dateOptions === null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Unique to Tweets component
  // Never send unique states to Card components
  const [increment, setIncrement] = useState();
  const [biggestTopicChange, setBiggestTopicChange] = useState();
  const [aggregate, setAggregate] = useState();

  // Country Select
  const [countrySelectConfig, setCountrySelectConfig] = useState({
    isIncluded: currentCountry === "LAC",
    value: ["LAC"],
    options: getCountriesAsSelectOptions(language),
  });

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  function handleCountrySelectChange(newValue) {
    setCountrySelectConfig({
      ...countrySelectConfig,
      value: newValue,
    });
  }

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);
        setOptions(null);

        const response = await getTweetsVolumeData(
          currentCountry,
          selectedYear
        );

        if (response) {
          const transformedDatesData = transformNameCountries(
            response.data,
            language
          );

          let countries = Object.keys(transformedDatesData);
          countries.shift();

          let series = seriesValues(transformedDatesData);

          if (country === "LAC") {
            countries = countries.filter((c) =>
              isCountryIncluded(c, countrySelectConfig.value, language)
            );

            series = series.filter((line) =>
              isCountryIncluded(line.name, countrySelectConfig.value, language)
            );
          }
          /* const lineConfig = getSimpleLineConfig(
              currentCountry,
              countries,
              colorRBG,
              transformedDatesData.dates,
              false,
              true,
              language
            ) */
          const options = {
            ...getSimpleLineConfig(
              currentCountry,
              countries,
              colorRBG,
              transformedDatesData.dates,
              false,
              true,
              language
            ),
            grid:
              selectedCountry === "LAC" ? GRID_CONFIG_WITH_LEGEND : GRID_CONFIG,
            series: series,
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const isPercentage = false;
                const areColorsSwitched = false;
                if (currentCountry === "LAC") {
                  return generateMultiplLineTooltip(
                    params,
                    transformedDatesData,
                    isPercentage,
                    areColorsSwitched,
                    language
                  );
                } else {
                  return generateLineToolTip(
                    params,
                    "tweets",
                    selectedYear,
                    transformedDatesData,
                    dateOptions.yearOptions,
                    isPercentage,
                    language
                  );
                }
              },
            },
          };
          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setOptions(null);
        setIsDataAvailable(false);
        setIsLoading(false);
        return;
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    selectedYear,
    isLoadingInitialData,
    currentCountry,
    countrySelectConfig,
  ]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      const isFirstYearSelected = selectedYear === dateOptions.firstYear;
      let promises;
      let callback;

      if (isMostRecentYear) {
        const previousMonth = getPreviousMonth(
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month,
          dateOptions.yearOptions
        );

        const lastMonthDataResponse = getMonthlyTweetVolume(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        const previousMonthDataResponse = getMonthlyTweetVolume(
          currentCountry,
          previousMonth.year,
          previousMonth.month
        );

        const topicsData = getMonthlyThematicEvolution(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        const topicDataPrevMonth = getMonthlyThematicEvolution(
          currentCountry,
          previousMonth.year,
          previousMonth.month
        );

        promises = [
          lastMonthDataResponse,
          previousMonthDataResponse,
          topicsData,
          topicDataPrevMonth,
        ];
        callback = (results) => {
          const diff = getPercentageDifference(
            results[0].data.value,
            results[1].data.value
          );
          setIncrement(diff);

          if (results[0].data.value === 0) setLastMonthData("0");
          else setLastMonthData(results[0].data.value);

          // topics
          const topicKeys = Object.keys(results[2].data);
          let maxAbsoluteDifference = -1;
          let maxAbsoluteDifferenceTopic;
          let difference;
          topicKeys.forEach((t) => {
            difference = results[2].data[t] - results[3].data[t];
            if (Math.abs(difference) > Math.abs(maxAbsoluteDifference)) {
              maxAbsoluteDifference = difference;
              maxAbsoluteDifferenceTopic = t;
            }
          });

          setBiggestTopicChange(maxAbsoluteDifferenceTopic);
        };
      } else if (isFirstYearSelected) {
        const tweetsVolumePromise = getTweetsVolumeData(
          currentCountry,
          selectedYear
        );
        promises = [tweetsVolumePromise];
        callback = (results) => {
          let sumCurrentYear;
          let length;
          let avgCurrentYear;

          if (currentCountry !== "LAC") {
            // tweets
            sumCurrentYear = results.data.data.reduce((a, b) => a + b, 0);
            length = results.data.data.length;
          } else {
            sumCurrentYear = results[0].data["LAC"].reduce((a, b) => a + b, 0);
            length = results[0].data["LAC"].length;
          }

          avgCurrentYear = sumCurrentYear / length || 0;

          setIncrement("--");
          setLastMonthData(avgCurrentYear.toFixed(0));
          setAggregate(sumCurrentYear);
        };
      } else {
        const currentYearPromise = getTweetsVolumeData(
          currentCountry,
          selectedYear
        );

        const previousYearPromise = getTweetsVolumeData(
          currentCountry,
          parseInt(selectedYear) - 1
        );

        promises = [currentYearPromise, previousYearPromise];
        callback = (results) => {
          let sumCurrentYear, sumPreviousYear;
          let length;
          let avgCurrentYear;
          let diff;

          if (currentCountry !== "LAC") {
            // tweets
            sumCurrentYear = results[0].data.data.reduce((a, b) => a + b, 0);
            sumPreviousYear = results[1].data.data.reduce((a, b) => a + b, 0);
            length = results[0].data.data.length;
          } else {
            sumCurrentYear = results[0].data["LAC"].reduce((a, b) => a + b, 0);
            sumPreviousYear = results[1].data["LAC"].reduce((a, b) => a + b, 0);
            length = results[0].data["LAC"].length;
          }

          avgCurrentYear = sumCurrentYear / length || 0;
          diff = getPercentageDifference(sumCurrentYear, sumPreviousYear);

          setIncrement(diff);
          setLastMonthData(avgCurrentYear.toFixed(0));
          setAggregate(sumCurrentYear);
        };
      }

      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setIncrement("--");
          setAggregate("--");
          setBiggestTopicChange("--");
          setLastMonthData("--");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountrySelectConfig({
        ...countrySelectConfig,
        isIncluded: selectedCountry === "LAC",
      });
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
    setCountrySelectConfig({
      ...countrySelectConfig,
      options: getCountriesAsSelectOptions(language),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  function TweetCardContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle(isMostRecentYear)}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? lastMonthData : aggregate,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            increment,
            biggestTopicChange
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          includesCountrySelect={countrySelectConfig.isIncluded}
          handleCountrySelectChange={handleCountrySelectChange}
          countrySelectDefaultValue={countrySelectConfig.value}
          countrySelectOptions={countrySelectConfig.options}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "VolumenDeTweets" : "TweetVolume",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle(isMostRecentYear)}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <TweetCardContent />}
    </CardContainer>
  );
}
